import { Add, Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import { SetStateAction, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAlert } from '../../context/AlertContext';
import { ILegalClient, initLegalClient } from '../../Interfaces/ILegalClient';
import {
  ILegalClientRelationship,
  initLegalClientRelationship,
  issueMembers,
} from '../../Interfaces/ILegalClientRelationship/ILegalClientRelationship';
import { State } from '../../redux/mainReducer';
import { LegalClientSelectDialog } from '../LegalClientSelectDialog';

interface IProps {
  editedLegalClientId?: number;
  selectedRelationship?: ILegalClientRelationship;
  legalClients: ILegalClient[];
  relationships: ILegalClientRelationship[];
  setRelationships: React.Dispatch<SetStateAction<ILegalClientRelationship[]>>;
  unmount: () => void;
}

const defaultRequiredMember = {
  ...initLegalClient(),
  FirstName: 'Neuer',
  LastName: 'Mandant',
};

export const UpdateOrCreateRelationshipDialog = ({
  editedLegalClientId,
  selectedRelationship,
  legalClients,
  relationships,
  setRelationships,
  unmount,
}: IProps) => {
  const [isSelectDialogMount, setIsSelectDialogMount] = useState(false);

  const relationshipTypes = useSelector((state: State) =>
    state.relationshipTypeArray.storeRelationshipType.filter(
      type =>
        type.idRelationshipTypeScope === 2 || type.idRelationshipTypeScope === 3
    )
  );
  const relationshipRoles = useSelector(
    (state: State) => state.relationshipRoleArray.storeRelationshipRole
  );

  const relationshipTypeHasRoleMap = useSelector(
    (state: State) =>
      state.relationshipTypeHasRoleArray
        .storeRelationshipTypeHasRelationshipRole
  );

  const issuePossibleRoles = (relationshipTypeId: number) =>
    relationshipRoles.filter(
      r =>
        relationshipTypeHasRoleMap.find(
          hr =>
            hr.idRelationshipType === relationshipTypeId &&
            hr.idRelationshipRole === r.idRelationshipRole
        ) !== undefined
    );

  const {
    idRelationshipType: defaultRelationshipTypeId,
    RelationshipType: defaultRelationshipType,
  } = relationshipTypes[0];
  const {
    idRelationshipRole: defaultRelationshipRoleId,
    RelationshipRole: defaultRelationshipRole,
  } = issuePossibleRoles(defaultRelationshipTypeId)[0];

  const [currentRelationship, setCurrentRelationship] =
    useState<ILegalClientRelationship>(
      selectedRelationship ??
        initLegalClientRelationship(
          legalClients.find(lc => lc.idLegalClient === editedLegalClientId) ??
            defaultRequiredMember,
          defaultRelationshipTypeId,
          defaultRelationshipType,
          defaultRelationshipRoleId,
          defaultRelationshipRole
        )
    );

  const { showAlert } = useAlert();

  const possibleRoles = useMemo(
    () => issuePossibleRoles(currentRelationship.idRelationshipType),
    [currentRelationship.idRelationshipType]
  );

  const onSubmit = () => {
    try {
      if (!!selectedRelationship) {
        setRelationships(
          relationships.map(r => {
            if (r.idRelationship === currentRelationship.idRelationship) {
              return currentRelationship;
            } else return r;
          })
        );
      } else {
        setRelationships([...relationships, currentRelationship]);
      }

      unmount();
    } catch (error) {
      console.error(error);
      showAlert({
        text: 'Die Beziehung konnte nicht hinzugefügt werden!',
        severity: 'error',
      });
    }
  };

  const onSelect = (selectedLegalClients: ILegalClient[]) => {
    const oldMembers = currentRelationship.Members;

    const { idRelationshipRole, RelationshipRole } = issuePossibleRoles(
      currentRelationship.idRelationshipType
    )[0];

    const newMembers = issueMembers(
      selectedLegalClients.filter(
        lc => !oldMembers.some(m => m.idLegalClient === lc.idLegalClient)
      ),
      idRelationshipRole,
      RelationshipRole
    );

    setCurrentRelationship({
      ...currentRelationship,
      Members: [...oldMembers, ...newMembers],
    });
  };

  const onRemove = (index: number) =>
    setCurrentRelationship({
      ...currentRelationship,
      Members: [...currentRelationship.Members.filter((_, mi) => mi !== index)],
    });

  return (
    <>
      {isSelectDialogMount && (
        <LegalClientSelectDialog
          blockedIds={editedLegalClientId ? [editedLegalClientId] : []}
          selectedIds={currentRelationship.Members.map(x => x.idLegalClient)}
          setSelected={onSelect}
          allLegalClients={legalClients}
          unmount={() => setIsSelectDialogMount(false)}
        />
      )}

      <Dialog onClose={unmount} open={true} fullWidth maxWidth='sm'>
        <DialogTitle>
          {!!selectedRelationship
            ? 'Beziehung bearbeiten'
            : 'Beziehung hinzufügen'}
        </DialogTitle>
        <DialogContent sx={{ minHeight: '40vh' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              required
              label='Relationstyp'
              select
              fullWidth
              size='small'
              sx={{ mt: 1 }}
              value={currentRelationship.idRelationshipType}
              onChange={({ target }) => {
                const newRelationship = editedLegalClientId
                  ? currentRelationship
                  : initLegalClientRelationship(
                      legalClients.find(
                        lc => lc.idLegalClient === editedLegalClientId
                      ) ?? defaultRequiredMember,
                      defaultRelationshipTypeId,
                      defaultRelationshipType,
                      defaultRelationshipRoleId,
                      defaultRelationshipRole
                    );

                const newRelationshipTypeId = Number(target.value);
                const newRelationshipType = relationshipTypes.find(
                  t => t.idRelationshipType === newRelationshipTypeId
                )!.RelationshipType;

                newRelationship.idRelationshipType = newRelationshipTypeId;
                newRelationship.RelationshipType = newRelationshipType;

                const { RelationshipRole, idRelationshipRole } =
                  issuePossibleRoles(newRelationship.idRelationshipType)[0];

                setCurrentRelationship({
                  ...newRelationship,
                  Members: newRelationship.Members.map(m => ({
                    ...m,
                    idRelationshipRole: idRelationshipRole,
                    RelationshipRole: RelationshipRole,
                  })),
                });
              }}
            >
              {relationshipTypes.map((type, i) => (
                <MenuItem key={i} value={type.idRelationshipType}>
                  {type.RelationshipType}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box my={2}>
            <Typography variant='h6' mb={1}>
              Personen
              <IconButton
                sx={{ float: 'right' }}
                onClick={() => setIsSelectDialogMount(true)}
              >
                <Add />
              </IconButton>
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={200}>Rolle</TableCell>
                    <TableCell>Person</TableCell>
                    <TableCell width={90}>Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentRelationship.Members.map((m, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <TextField
                          label='Rolle'
                          select
                          fullWidth
                          size='small'
                          value={m.idRelationshipRole}
                          onChange={e => {
                            const selectedRoleId = Number(e.target.value);
                            const selectedRole = relationshipRoles.find(
                              r => r.idRelationshipRole === selectedRoleId
                            );

                            const newMembers = _.cloneDeep(
                              currentRelationship.Members
                            );

                            newMembers[i] = {
                              ...newMembers[i],
                              idRelationshipRole:
                                selectedRole!.idRelationshipRole,
                              RelationshipRole: selectedRole!.RelationshipRole,
                            };

                            setCurrentRelationship({
                              ...currentRelationship,
                              Members: newMembers,
                            });
                          }}
                        >
                          {possibleRoles.map((r, i) => (
                            <MenuItem key={i} value={r.idRelationshipRole}>
                              {r.RelationshipRole}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        {m.LegalClient?.FirstName} {m.LegalClient?.LastName}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size='small'
                          disabled={
                            editedLegalClientId
                              ? m.idLegalClient === editedLegalClientId
                              : m.idLegalClient === -1
                          }
                          onClick={() => onRemove(i)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => unmount()}>
            Abbruch
          </Button>
          <Button
            variant='contained'
            onClick={onSubmit}
            disabled={currentRelationship.Members.length === 0}
          >
            {!!selectedRelationship ? 'Speichern' : 'Hinzufügen'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
